import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioExtensaoService } from '../../../services/usuarioExtensaoService';
import { usuarioService } from '../../../services/usuarioService';
import { extensaoService } from '../../../services/extensaoService';

const initialState = {
  usuario_id: '',
  extensao_id: '',
  data_inicio: '',
  data_fim: '',
  observacoes: '',
  ativo: true
};

const UsuarioExtensaoCreate = () => {
  const [form, setForm] = useState(initialState);
  const [usuarios, setUsuarios] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadUsuarios();
    loadExtensoes();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await usuarioService.list({ limit: 1000 });
      setUsuarios(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    }
  };

  const loadExtensoes = async () => {
    try {
      // includeAll=true para garantir que Super Admin veja todas as extensões
      const response = await extensaoService.list({ limit: 1000, includeAll: true });
      setExtensoes(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar extensões:', err);
    }
  };

  const validate = () => {
    if (!form.usuario_id) return 'Selecione um usuário.';
    if (!form.extensao_id) return 'Selecione uma extensão.';
    
    if (form.data_inicio && form.data_fim) {
      const dataInicio = new Date(form.data_inicio);
      const dataFim = new Date(form.data_fim);
      if (dataFim <= dataInicio) {
        return 'A data de fim deve ser posterior à data de início.';
      }
    }
    
    return '';
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'checkbox' ? checked : value 
    });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setShowConfirm(false);
      setLoading(true);
      setError('');
      
      const accessData = {
        usuario_id: parseInt(form.usuario_id),
        extensao_id: parseInt(form.extensao_id),
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null,
        observacoes: form.observacoes.trim() || null,
        ativo: form.ativo
      };
      
      await usuarioExtensaoService.create(accessData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/seguranca/usuarios/extensoes');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao criar acesso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Criar Acesso Usuário-Extensão</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Usuário *</label>
          <select
            name="usuario_id"
            value={form.usuario_id}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione um usuário</option>
            {usuarios.map(usuario => (
              <option key={usuario.id} value={usuario.id}>
                {usuario.nome} ({usuario.email})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Extensão *</label>
          <select
            name="extensao_id"
            value={form.extensao_id}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione uma extensão</option>
            {extensoes.map(extensao => (
              <option key={extensao.idExtensao} value={extensao.idExtensao}>
                {extensao.nome} - {extensao.cidade}/{extensao.uf}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Data de Início</label>
            <input
              type="date"
              name="data_inicio"
              value={form.data_inicio}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Data de Fim</label>
            <input
              type="date"
              name="data_fim"
              value={form.data_fim}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Observações</label>
          <textarea
            name="observacoes"
            value={form.observacoes}
            onChange={handleChange}
            rows={4}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Observações sobre este acesso (opcional)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={true}
                checked={form.ativo === true}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: true }))}
                className="mr-2"
              />
              Ativo
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={false}
                checked={form.ativo === false}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: false }))}
                className="mr-2"
              />
              Inativo
            </label>
          </div>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Acesso criado com sucesso!</div>}
        
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/seguranca/usuarios/extensoes')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading}
           aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Criação"
        message="Deseja realmente criar este acesso usuário-extensão?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default UsuarioExtensaoCreate;
